import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useProfiles } from '@/hooks/useProfiles';
import { useCooperativeEligibility } from '@/hooks/useCooperativeEligibility';
import { AlertCircle, CheckCircle2, Users, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface CooperativeMemberDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CooperativeMemberDialog: FC<CooperativeMemberDialogProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { profile } = useProfiles();
  const navigate = useNavigate();
  const { 
    eligibilityStatus, 
    loading 
  } = useCooperativeEligibility();

  const canApply = eligibilityStatus.isEligible;
  const isHost = profile?.host_approved || false;

  // Calculate missing requirements
  const missingRequirements: string[] = [];
  if (!eligibilityStatus.hasListings) {
    missingRequirements.push('Mindestens eine aktive Unterkunft erforderlich');
  }
  if (!eligibilityStatus.hasPaidBookings) {
    missingRequirements.push('Mindestens eine abgeschlossene bezahlte Buchung erforderlich');
  }
  if (eligibilityStatus.platformFeesCollected === 0) {
    missingRequirements.push('Noch keine Plattformgebühren eingezogen');
  }

  const handleProceed = () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (!isHost) {
      onClose();
      // Show become host dialog instead
      window.dispatchEvent(new CustomEvent('open-become-host-dialog'));
      return;
    }

    onClose();
    navigate('/cooperative/apply');
  };

  const handleDormantApplication = () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    onClose();
    navigate('/cooperative/apply?dormant=true');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Users className="h-6 w-6 text-primary" />
            Genossenschaftsmitglied werden
          </DialogTitle>
          <DialogDescription className="text-base">
            Werde Teil der Fair-Stays Genossenschaft und profitiere von langfristigen Vorteilen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Benefits */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <h3 className="font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Vorteile der Mitgliedschaft
            </h3>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>✓ Reduzierte Plattformgebühren (langfristig)</li>
              <li>✓ Mitbestimmung bei strategischen Entscheidungen</li>
              <li>✓ Anteil am Gewinn der Genossenschaft</li>
              <li>✓ Exklusive Weiterbildungs- und Networking-Angebote</li>
            </ul>
          </div>

          {/* Eligibility Check */}
          {isHost && (
            <div className="space-y-3">
              <h3 className="font-semibold">Voraussetzungen für die Mitgliedschaft</h3>
              
              {loading ? (
                <div className="text-center p-4">
                  <div className="loading-spinner mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Prüfe Voraussetzungen...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {canApply ? (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription>
                        Du erfüllst alle Voraussetzungen für die Genossenschaftsmitgliedschaft!
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Nicht alle Genossenschaftsbedingungen erfüllt. Du kannst trotzdem einen Antrag stellen, der in Ruhezustand versetzt wird.
                        </AlertDescription>
                      </Alert>

                      <div className="space-y-2 mt-3">
                        <p className="text-sm font-medium">Fehlende Voraussetzungen:</p>
                        {missingRequirements.map((requirement, index) => (
                          <div key={index} className="flex items-start gap-2 text-sm">
                            <XCircle className="h-4 w-4 text-destructive mt-0.5" />
                            <span className="text-muted-foreground">{requirement}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Not a Host */}
          {!isHost && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Um Genossenschaftsmitglied zu werden, musst du zunächst als Gastgeber freigeschaltet sein.
              </AlertDescription>
            </Alert>
          )}

          {/* Application Info */}
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
            <h4 className="font-semibold text-sm mb-2">Mitgliedschaftsgebühr:</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Eintrittsgeld und Betrag der Genossenschaftsanteile werden zu späterem Zeitpunkt bekannt gegeben
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          
          {!isHost ? (
            <Button onClick={handleProceed}>
              Zuerst Gastgeber werden
            </Button>
          ) : canApply ? (
            <Button onClick={handleProceed}>
              Antrag stellen
            </Button>
          ) : (
            <Button onClick={handleDormantApplication} variant="secondary">
              Antrag in Ruhezustand stellen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
